/**
 * DR Cart Gifts Reconciler (Dawn-native selectable gift pools)
 *
 * Single-writer, intent-based reconciliation for predictable cart behavior.
 * The engine computes desired gift state and applies one cart write if needed.
 * Dawn components remain the primary renderer by default.
 */
(function () {
  'use strict';

  const GIFT_ENGINE_SOURCE = 'dr-gifts-engine';
  const DR_GIFTS_DEBUG_REVERSE = false;

  class PromiseQueue {
    constructor() {
      this._queue = Promise.resolve();
      this._pending = 0;
    }
    get isBusy() {
      return this._pending > 0;
    }
    enqueue(fn) {
      this._pending++;
      updateButtonStates();
      const task = this._queue.catch(() => {}).then(() => fn());
      this._queue = task.catch(() => {});
      return task.finally(() => {
        this._pending--;
        updateButtonStates();
      });
    }
  }

  const fallbackQueue = new PromiseQueue();
  let externalPending = 0;
  let externalIdleResolvers = [];
  let checkoutArmed = false;
  let cachedConfig = null;
  let warnedNoConfig = false;
  let reverseMorphActive = false;
  let membersReopenPending = false;
  let membersReopenState = null;
  let activeCheckoutPromise = null;
  let buttonStateFrame = 0;
  let pendingBusyState = false;

  function getSettlementCoordinator() {
    return window.drCartSettlementCoordinator || null;
  }

  function getGiftsUi() {
    return window.drCartGiftsUi || null;
  }

  function getCartStore() {
    return window.drCartStore || null;
  }

  function getTransport() {
    return window.drShopifyCartTransport || null;
  }

  function getRulesEngine() {
    return window.drGiftRulesEngine || null;
  }

  function getSectionsRenderer() {
    return window.drCartSectionsRenderer || null;
  }

  function enqueueSystemTask(task) {
    const settlementCoordinator = getSettlementCoordinator();
    if (settlementCoordinator?.runNowOrEnqueue) {
      return settlementCoordinator.runNowOrEnqueue(task);
    }
    if (settlementCoordinator?.enqueueMutation) {
      return settlementCoordinator.enqueueMutation(task);
    }
    return fallbackQueue.enqueue(task);
  }

  function enqueueReconcilerTask(task) {
    return enqueueSystemTask(task);
  }

  function beginExternalMutation() {
    externalPending++;
    updateButtonStates();
    let ended = false;
    return function endExternalMutation() {
      if (ended) return;
      ended = true;
      externalPending = Math.max(0, externalPending - 1);
      updateButtonStates();
      if (externalPending === 0) {
        externalIdleResolvers.splice(0).forEach((resolve) => resolve());
      }
    };
  }

  function runTrackedMutation(task) {
    if (typeof task !== 'function') return Promise.resolve();
    const settlementCoordinator = getSettlementCoordinator();
    if (settlementCoordinator?.runNowOrEnqueue || settlementCoordinator?.enqueueMutation) {
      return enqueueSystemTask(task);
    }
    const endExternalMutation = beginExternalMutation();
    return Promise.resolve()
      .then(() => task())
      .finally(() => {
        endExternalMutation();
      });
  }

  async function whenIdle() {
    while (true) {
      const settlementCoordinator = getSettlementCoordinator();
      if (settlementCoordinator?.whenIdle) {
        await settlementCoordinator.whenIdle();
      }
      const q1 = fallbackQueue._queue;
      await q1;
      if (externalPending > 0) {
        await new Promise((resolve) => externalIdleResolvers.push(resolve));
        continue;
      }
      const q2 = fallbackQueue._queue;
      await q2;
      if (q2 === fallbackQueue._queue && externalPending === 0) return;
    }
  }

  function armCheckout() {
    checkoutArmed = true;
    updateButtonStates();
  }

  function disarmCheckout() {
    checkoutArmed = false;
    updateButtonStates();
  }

  function isSystemBusy() {
    return checkoutArmed || fallbackQueue.isBusy || externalPending > 0 || !!getSettlementCoordinator()?.isBusy;
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    externalPending = 0;
    checkoutArmed = false;
    externalIdleResolvers.splice(0).forEach((resolve) => resolve());
    updateButtonStates();
  });
  document.addEventListener('dr:cart-settlement-state', updateButtonStates);

  function normalizeIds(values) {
    return Array.from(new Set((values || []).filter(Boolean).map(Number).filter(Boolean)));
  }

  function normalizePool(rawPool, defaultThreshold) {
    const variants = normalizeIds(
      Array.isArray(rawPool?.variants)
        ? rawPool.variants
        : [rawPool?.variantId]
    );
    const requestedIndex = Math.max(0, Number(rawPool?.defaultIndex) || 0);
    const defaultIndex = requestedIndex < variants.length ? requestedIndex : 0;
    return Object.freeze({
      variants: Object.freeze(variants),
      variantId: variants[defaultIndex] || variants[0] || 0,
      defaultIndex,
      threshold: Number(rawPool?.threshold) || defaultThreshold,
      useDiscountedTotal: rawPool?.useDiscountedTotal === true,
    });
  }

  function normalizeConfig(parsed) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.normalizeConfig) {
      return rulesEngine.normalizeConfig(parsed);
    }
    if (!parsed) return null;
    const modules = parsed.modules || {};
    const freeIds = normalizeIds(parsed.free);
    const bonus = normalizePool(parsed.bonus, 80000);
    const bonus2 = normalizePool(parsed.bonus2, 120000);
    const bonus3 = normalizePool(parsed.bonus3, 160000);
    const allGiftVariantIds = Object.freeze(
      normalizeIds(
        Array.isArray(parsed.allGiftVariantIds) && parsed.allGiftVariantIds.length > 0
          ? parsed.allGiftVariantIds
          : [...freeIds, ...bonus.variants, ...bonus2.variants, ...bonus3.variants]
      )
    );
    return Object.freeze({
      modules: Object.freeze({
        free: modules.free !== false,
        bonus1: modules.bonus1 !== false,
        bonus2: modules.bonus2 !== false,
        bonus3: modules.bonus3 !== false,
        membersTab: modules.membersTab !== false,
        newMemberTab: modules.newMemberTab !== false,
      }),
      free: Object.freeze(freeIds),
      bonus,
      bonus2,
      bonus3,
      defaultFreeIndex: Number(parsed.defaultFreeIndex) || 0,
      couponCode: String(parsed.couponCode || '').trim().toUpperCase(),
      sections: Object.freeze(parsed.sections || ['cart-drawer', 'cart-icon-bubble']),
      sectionsUrl: parsed.sectionsUrl || '/cart',
      allGiftVariantIds,
    });
  }

  function getConfig() {
    if (!cachedConfig && window.DR_GIFTS_CONFIG) {
      cachedConfig = normalizeConfig(window.DR_GIFTS_CONFIG);
    }
    if (!cachedConfig && !warnedNoConfig) {
      warnedNoConfig = true;
      console.warn('[DR Gifts] Missing window.DR_GIFTS_CONFIG (config snippet not loaded?)');
    }
    return cachedConfig;
  }

  function isFullCartState(obj) {
    return (
      obj &&
      typeof obj === 'object' &&
      Array.isArray(obj.items) &&
      typeof obj.total_price === 'number' &&
      typeof obj.item_count === 'number'
    );
  }

  function isPartialAddState(obj) {
    if (!obj || typeof obj !== 'object') return false;
    const hasVariant =
      Number.isFinite(Number(obj.variant_id)) ||
      Number.isFinite(Number(obj.id));
    const hasSections = !!obj.sections && typeof obj.sections === 'object';
    return hasVariant && hasSections;
  }

  function isModuleEnabled(config, key) {
    if (!config || !config.modules || typeof config.modules !== 'object') return true;
    return config.modules[key] !== false;
  }

  function buildAllGiftIdSet(config) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.buildAllGiftIdSet) {
      return rulesEngine.buildAllGiftIdSet(config);
    }
    const ids = new Set(config.free);
    (config.bonus?.variants || []).forEach((variantId) => ids.add(variantId));
    (config.bonus2?.variants || []).forEach((variantId) => ids.add(variantId));
    (config.bonus3?.variants || []).forEach((variantId) => ids.add(variantId));
    return ids;
  }

  function buildFreeGiftIdSet(config) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.buildFreeGiftIdSet) {
      return rulesEngine.buildFreeGiftIdSet(config);
    }
    return new Set(config.free);
  }

  function computeEligibleTotal(cart, config, allGiftSet) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.computeEligibleTotal) {
      return rulesEngine.computeEligibleTotal(cart, config, allGiftSet);
    }
    const useDiscountedTotal =
      config.bonus?.useDiscountedTotal === true ||
      config.bonus2?.useDiscountedTotal === true ||
      config.bonus3?.useDiscountedTotal === true;
    if (useDiscountedTotal) {
      return Number(cart.total_price) || 0;
    }
    return (cart.items || []).reduce((sum, item) => {
      if (allGiftSet.has(item.variant_id)) return sum;
      return sum + (Number(item.original_line_price) || 0);
    }, 0);
  }

  function getPoolSelection(cart, pool, overrideVariantId) {
    const poolSet = new Set(pool?.variants || []);
    const overrideId = Number(overrideVariantId || 0);
    if (overrideId && poolSet.has(overrideId)) return overrideId;
    const selectedLine = (cart?.items || []).find(
      (item) => poolSet.has(Number(item.variant_id)) && Number(item.quantity) > 0
    );
    return selectedLine?.variant_id || pool?.variants?.[pool?.defaultIndex || 0] || pool?.variants?.[0] || null;
  }

  function computeDesiredGiftState(cart, config, freeSet, allGiftSet, selectionOverrides) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.computeDesiredGiftState) {
      return rulesEngine.computeDesiredGiftState(cart, config, freeSet, allGiftSet, selectionOverrides);
    }
    const freeModuleOn = isModuleEnabled(config, 'free');
    const bonus1ModuleOn = isModuleEnabled(config, 'bonus1');
    const bonus2ModuleOn = isModuleEnabled(config, 'bonus2');
    const bonus3ModuleOn = isModuleEnabled(config, 'bonus3');
    const hasPaid = cart.items.some((item) => !allGiftSet.has(item.variant_id));
    const overrides = selectionOverrides && typeof selectionOverrides === 'object'
      ? selectionOverrides
      : { free: Number(selectionOverrides || 0) || null };
    if (!hasPaid) {
      return {
        desiredFree: null,
        desiredBonus: null,
        desiredBonus2: null,
        desiredBonus3: null,
        desiredBonusQty: 0,
        desiredBonus2Qty: 0,
        desiredBonus3Qty: 0,
      };
    }

    const freeInCart = cart.items.filter((item) => freeSet.has(item.variant_id));
    const desiredFree = freeModuleOn
      ? (
        overrides.free && freeSet.has(Number(overrides.free))
          ? Number(overrides.free)
          : freeInCart.length === 1
          ? freeInCart[0].variant_id
          : config.free[config.defaultFreeIndex] || config.free[0] || null
      )
      : null;

    const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
    const desiredBonus = bonus1ModuleOn && config.bonus.variants.length && eligibleTotal >= config.bonus.threshold
      ? getPoolSelection(cart, config.bonus, overrides.bonus)
      : null;
    const desiredBonus2 = bonus2ModuleOn && config.bonus2.variants.length && eligibleTotal >= config.bonus2.threshold
      ? getPoolSelection(cart, config.bonus2, overrides.bonus2)
      : null;
    const desiredBonus3 = bonus3ModuleOn && config.bonus3.variants.length && eligibleTotal >= config.bonus3.threshold
      ? getPoolSelection(cart, config.bonus3, overrides.bonus3)
      : null;
    return {
      desiredFree,
      desiredBonus,
      desiredBonus2,
      desiredBonus3,
      desiredBonusQty: desiredBonus ? 1 : 0,
      desiredBonus2Qty: desiredBonus2 ? 1 : 0,
      desiredBonus3Qty: desiredBonus3 ? 1 : 0,
    };
  }

  function buildSyntheticEmptyCart() {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.buildSyntheticEmptyCart) {
      return rulesEngine.buildSyntheticEmptyCart();
    }
    return {
      items: [],
      total_price: 0,
      item_count: 0,
    };
  }

  async function planAtomicAddGiftLines(opts = {}) {
    const config = getConfig();
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.planAtomicAddGiftLines) {
      return Promise.resolve(
        rulesEngine.planAtomicAddGiftLines({
          config,
          cart: isFullCartState(opts.cart) ? opts.cart : null,
          variantId: opts.variantId,
          quantity: opts.quantity,
          assumeEmpty: opts.assumeEmpty === true,
        })
      );
    }
    if (!config || config.free.length === 0 || !isModuleEnabled(config, 'free')) {
      return { giftLines: [], reason: 'no_config' };
    }
    return { giftLines: [], reason: 'rules_engine_missing' };
  }

  async function fetchCart() {
    const transport = getTransport();
    if (transport?.fetchCart) {
      return transport.fetchCart();
    }
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart (${response.status})`);
    return response.json();
  }

  async function fetchCartForPostAdd(addedVariantId, attempts = 3) {
    const transport = getTransport();
    if (transport?.fetchCartForPostAdd) {
      return transport.fetchCartForPostAdd(addedVariantId, attempts);
    }
    const targetVariantId = Number(addedVariantId || 0);
    const maxAttempts = Math.max(1, Number(attempts) || 1);

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const candidate = await fetchCart();
        if (!isFullCartState(candidate)) continue;
        if (
          !targetVariantId ||
          candidate.items.some((item) => Number(item.variant_id) === targetVariantId)
        ) {
          return candidate;
        }
      } catch (_) {
        // Fall back to existing partial reconciliation path when cart fetch is transiently unavailable.
      }

      if (attempt < maxAttempts) {
        await delay(80 * attempt);
      }
    }

    return null;
  }

  function extractSectionInnerHTML(sectionHtml, selector = '.shopify-section') {
    const sectionsRenderer = getSectionsRenderer();
    if (sectionsRenderer?.extractSectionInnerHTML) {
      return sectionsRenderer.extractSectionInnerHTML(sectionHtml, selector);
    }
    if (typeof sectionHtml !== 'string' || !sectionHtml) return null;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return doc.querySelector(selector)?.innerHTML || null;
  }

  function patchSectionTarget(target, sectionHtml, selector) {
    const sectionsRenderer = getSectionsRenderer();
    if (sectionsRenderer?.patchSectionTarget) {
      return sectionsRenderer.patchSectionTarget(target, sectionHtml, selector);
    }
    if (!target) return false;
    const innerHtml = extractSectionInnerHTML(sectionHtml, selector);
    if (innerHtml == null) return false;
    target.innerHTML = innerHtml;
    return true;
  }

  function afterCartSectionsRendered(cartData, opts = {}) {
    renderCouponNote();
    // Lets the compact widget keep an open bottom sheet across the re-render.
    document.dispatchEvent(new CustomEvent('dr:cart-sections-rendered', { detail: { source: opts.source || '' } }));
    const mode = opts.membersMode || 'restore-if-open';
    const widget = getActiveGiftsWidget();
    const membersRoot = widget?.querySelector?.('[data-dr-members]') || document.querySelector('cart-drawer [data-dr-members]');

    if (mode === 'reset-to-gifts') {
      if (!widget || !membersRoot) return false;
      const resetState = { activeTab: 'gifts', membersOpen: false, returnPending: false };
      writeMembersUiState(resetState);
      restoreMembersUiState(resetState);
      return true;
    }

    const config = getConfig();
    const pendingState = membersReopenPending ? membersReopenState : readMembersUiState();
    let restored = false;
    if (pendingState && normalizeTab(pendingState.activeTab, config) !== 'gifts') {
      const activeTab = normalizeTab(pendingState.activeTab, config);
      restored = restoreMembersUiState(pendingState);
      if (activeTab === 'members') {
        document.dispatchEvent(new CustomEvent('dr:members-rerendered', {
          detail: {
            activeTab,
            source: opts.source || 'dr-cart-gifts-reconciler',
          },
        }));
      }
    }
    membersReopenPending = false;
    membersReopenState = null;
    return restored;
  }

  function renderWithDawn(cartData) {
    const sectionsRenderer = getSectionsRenderer();
    if (sectionsRenderer?.render) {
      return sectionsRenderer.render(cartData, {
        afterRender() {
          afterCartSectionsRendered(cartData, {
            source: 'renderWithDawn',
            membersMode: 'restore-if-open',
          });
        },
      });
    }
    if (!cartData?.sections) return false;
    return false;
  }

  function nextAnimationFrame() {
    return new Promise((resolve) => requestAnimationFrame(resolve));
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getActiveGiftsWidget(root) {
    const giftsUi = getGiftsUi();
    if (giftsUi?.getWidget) {
      return giftsUi.getWidget(root);
    }
    if (root && typeof root.querySelector === 'function') {
      const localWidget = root.querySelector('.dr-gifts[data-dr-gifts-widget]');
      if (localWidget) return localWidget;
    }
    return document.querySelector('cart-drawer .dr-gifts[data-dr-gifts-widget], .dr-gifts[data-dr-gifts-widget]');
  }

  function getAvailableTabs(config) {
    const giftsUi = getGiftsUi();
    if (giftsUi?.getAvailableTabs) {
      return giftsUi.getAvailableTabs(config);
    }
    const cfg = config || getConfig() || {};
    const tabs = [];
    const giftsEnabled =
      (isModuleEnabled(cfg, 'free') && Array.isArray(cfg.free) && cfg.free.length > 0) ||
      (isModuleEnabled(cfg, 'bonus1') && Array.isArray(cfg.bonus?.variants) && cfg.bonus.variants.length > 0) ||
      (isModuleEnabled(cfg, 'bonus2') && Array.isArray(cfg.bonus2?.variants) && cfg.bonus2.variants.length > 0) ||
      (isModuleEnabled(cfg, 'bonus3') && Array.isArray(cfg.bonus3?.variants) && cfg.bonus3.variants.length > 0);
    if (giftsEnabled) tabs.push('gifts');
    if (isModuleEnabled(cfg, 'membersTab')) tabs.push('members');
    if (isModuleEnabled(cfg, 'newMemberTab')) tabs.push('new-customer');
    if (!tabs.length) tabs.push('gifts');
    return tabs;
  }

  function normalizeTab(tab, config) {
    const giftsUi = getGiftsUi();
    if (giftsUi?.normalizeTab) {
      return giftsUi.normalizeTab(tab, config || getConfig());
    }
    const value = String(tab || '').trim();
    const availableTabs = getAvailableTabs(config);
    if ((value === 'members' || value === 'new-customer' || value === 'gifts') && availableTabs.includes(value)) {
      return value;
    }
    return availableTabs[0];
  }

  function readMembersUiState() {
    const giftsUi = getGiftsUi();
    if (giftsUi?.readMembersUiState) {
      return giftsUi.readMembersUiState(getConfig());
    }
    try {
      const raw = sessionStorage.getItem('drGifts.membersUiState');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const config = getConfig();
      return {
        activeTab: normalizeTab(parsed.activeTab, config),
        membersOpen: parsed.membersOpen === true,
        returnPending: parsed.returnPending === true,
      };
    } catch (_) {
      return null;
    }
  }

  function writeMembersUiState(nextState) {
    const giftsUi = getGiftsUi();
    if (giftsUi?.writeMembersUiState) {
      return giftsUi.writeMembersUiState(nextState, getConfig());
    }
    try {
      const current = readMembersUiState() || { activeTab: 'gifts', membersOpen: false, returnPending: false };
      const config = getConfig();
      const merged = {
        activeTab: typeof nextState?.activeTab !== 'undefined' ? normalizeTab(nextState.activeTab, config) : current.activeTab,
        membersOpen: typeof nextState?.membersOpen === 'boolean' ? nextState.membersOpen : current.membersOpen,
        returnPending: typeof nextState?.returnPending === 'boolean' ? nextState.returnPending : current.returnPending,
      };
      sessionStorage.setItem('drGifts.membersUiState', JSON.stringify(merged));
      return merged;
    } catch (_) {
      return null;
    }
  }

  function restoreMembersUiState(state) {
    const widget = getActiveGiftsWidget();
    if (!widget) return false;
    const activeTab = normalizeTab(state?.activeTab, getConfig());
    const giftsUi = getGiftsUi();
    if (giftsUi?.applyTabState) {
      return giftsUi.applyTabState(widget, activeTab, getConfig());
    }
    widget.setAttribute('data-dr-active-tab', activeTab);
    widget.querySelectorAll('[data-dr-tab]').forEach((tabButton) => {
      const isActive = tabButton.getAttribute('data-dr-tab') === activeTab;
      tabButton.classList.toggle('dr-gifts__tab--active', isActive);
      tabButton.setAttribute('aria-selected', String(isActive));
      tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    widget.querySelectorAll('[data-dr-panel]').forEach((panel) => {
      const isActive = panel.getAttribute('data-dr-panel') === activeTab;
      panel.toggleAttribute('hidden', !isActive);
    });
    if (activeTab !== 'gifts') {
      widget.classList.remove('is-picker-open');
    }
    return true;
  }

  function requestMembersReopen(state = {}) {
    membersReopenPending = true;
    const config = getConfig();
    const preferredTab = getAvailableTabs(config).includes('members') ? 'members' : getAvailableTabs(config)[0];
    membersReopenState = {
      activeTab: typeof state.activeTab !== 'undefined' ? normalizeTab(state.activeTab, config) : preferredTab,
      membersOpen: preferredTab === 'members' ? state.membersOpen !== false : false,
      returnPending: state.returnPending === true,
    };
    writeMembersUiState(membersReopenState);
  }

  function setReverseDebugState(state, details) {
    if (!DR_GIFTS_DEBUG_REVERSE) return;
    const widget = getActiveGiftsWidget();
    if (!widget) return;
    const suffix = details ? ` | ${details}` : '';
    widget.setAttribute('data-dr-debug-reverse', `${state}${suffix}`);
  }

  function clearReverseDebugState(delayMs = 2500) {
    if (!DR_GIFTS_DEBUG_REVERSE) return;
    const widget = getActiveGiftsWidget();
    if (!widget) return;
    window.setTimeout(() => {
      const latestWidget = getActiveGiftsWidget();
      latestWidget?.removeAttribute('data-dr-debug-reverse');
    }, delayMs);
  }

  function waitForTransitionEnd(node, timeoutMs) {
    return new Promise((resolve) => {
      if (!node) {
        resolve();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        node.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (event) => {
        if (event.target === node) finish();
      };
      node.addEventListener('transitionend', onEnd);
      setTimeout(finish, timeoutMs);
    });
  }

  async function waitForOverlayClear(widget, maxMs = 1400) {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const root = widget || getActiveGiftsWidget();
      const hasBusy = !!root?.querySelector('[data-dr-gift-select][aria-busy="true"], [data-dr-coupon-activate][aria-busy="true"]');
      if (!hasBusy) {
        await nextAnimationFrame();
        await nextAnimationFrame();
        return;
      }
      await delay(40);
    }
    // Fallback so animation does not get stuck if busy flags linger.
    await delay(120);
  }

  function buildGhostForElement(source, className, containerRect) {
    if (!source) return null;
    const rect = source.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const clone = source.cloneNode(true);
    String(className || '')
      .split(/\s+/)
      .filter(Boolean)
      .forEach((token) => clone.classList.add(token));
    clone.removeAttribute('data-dr-gift-select');
    clone.removeAttribute('data-dr-gift-index');
    clone.removeAttribute('aria-pressed');
    clone.removeAttribute('aria-busy');
    clone.disabled = false;
    clone.querySelectorAll?.('[data-dr-gift-select]').forEach((node) => {
      node.removeAttribute('data-dr-gift-select');
      node.removeAttribute('data-dr-gift-index');
      node.removeAttribute('aria-pressed');
      node.removeAttribute('aria-busy');
      if ('disabled' in node) node.disabled = false;
    });
    clone.style.position = 'absolute';
    clone.style.top = `${rect.top - containerRect.top}px`;
    clone.style.left = `${rect.left - containerRect.left}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.margin = '0';
    clone.style.pointerEvents = 'none';
    clone.style.transformOrigin = 'top left';
    clone.style.transition = 'none';
    clone.style.opacity = '1';
    clone.style.transform = 'translate(0, 0) scale(1, 1)';
    return { node: clone, rect };
  }

  function captureReverseMorphContext(triggerButton) {
    setReverseDebugState('capture-start');
    const itemBtn = triggerButton?.closest('.dr-gifts__carousel-item');
    const widget = triggerButton?.closest('.dr-gifts[data-dr-gifts-widget]');
    const pickerGroup = itemBtn?.closest('[data-dr-picker-group]');
    if (!itemBtn || !widget || !widget.classList.contains('is-picker-open')) {
      setReverseDebugState('capture-skip', 'no-item-or-not-open');
      return null;
    }

    const drawer = widget.closest('cart-drawer');
    const widgetRect = widget.getBoundingClientRect();
    const layer = document.createElement('div');
    layer.className = 'dr-gifts-reverse-ghost-layer';
    layer.style.position = 'fixed';
    layer.style.top = `${widgetRect.top}px`;
    layer.style.left = `${widgetRect.left}px`;
    layer.style.width = `${widgetRect.width}px`;
    layer.style.height = `${widgetRect.height}px`;
    layer.style.overflow = 'hidden';
    layer.style.borderRadius = getComputedStyle(widget).borderRadius;
    layer.style.pointerEvents = 'none';
    layer.style.zIndex = '2147483000';
    layer.style.opacity = '0';

    const itemButtons = Array.from((pickerGroup || widget).querySelectorAll('.dr-gifts__carousel-item'));
    const selectedIndex = itemButtons.indexOf(itemBtn);
    const selectedGhost = buildGhostForElement(itemBtn, 'dr-gifts-reverse-ghost dr-gifts-reverse-ghost--selected', widgetRect);
    if (!selectedGhost) {
      return null;
    }
    layer.appendChild(selectedGhost.node);

    const otherGhosts = [];
    itemButtons.forEach((btn, index) => {
      if (btn === itemBtn) return;
      const ghost = buildGhostForElement(btn, 'dr-gifts-reverse-ghost dr-gifts-reverse-ghost--other', widgetRect);
      if (!ghost) return;
      ghost.distance = Math.abs(index - selectedIndex);
      otherGhosts.push(ghost);
      layer.appendChild(ghost.node);
    });

    document.body.appendChild(layer);
    if (drawer) drawer.classList.add('is-gifts-reverse-closing');
    setReverseDebugState('captured', `others:${otherGhosts.length}`);

    return {
      drawer,
      layer,
      selectedGhost,
      otherGhosts,
      tier: String(triggerButton?.dataset.drGiftTier || pickerGroup?.dataset.drPickerGroup || 'free'),
    };
  }

  function disposeReverseMorphContext(context) {
    if (!context) return;
    context.layer?.remove();
    context.drawer?.classList.remove('is-gifts-reverse-closing');
  }

  async function playReverseMorph(context) {
    if (!context) return;
    if (reverseMorphActive) {
      setReverseDebugState('skip', 'already-active');
      disposeReverseMorphContext(context);
      return;
    }
    reverseMorphActive = true;
    setReverseDebugState('play-start');

    const cleanup = () => {
      disposeReverseMorphContext(context);
      const widget = getActiveGiftsWidget(context.drawer);
      widget?.classList.remove('is-picker-open');
      widget?.classList.remove('is-closing', 'is-closing-play');
      setReverseDebugState('cleanup');
      clearReverseDebugState();
      reverseMorphActive = false;
    };

    try {
      setReverseDebugState('waiting-overlay');
      await waitForOverlayClear(getActiveGiftsWidget(context.drawer));
      setReverseDebugState('overlay-cleared');

      const widget = getActiveGiftsWidget(context.drawer);
      const target = widget?.querySelector(
        `[data-dr-gift-card][data-dr-gift-tier="${context.tier}"] .dr-gifts__view-product`
      );
      if (!widget || !target) {
        setReverseDebugState('skip', 'no-widget-or-target');
        cleanup();
        return;
      }
      context.targetNode = target;

      if (prefersReducedMotion()) {
        setReverseDebugState('skip', 'reduced-motion');
        cleanup();
        return;
      }

      context.layer.style.opacity = '1';
      widget.classList.remove('is-picker-open');
      widget.classList.add('is-closing');
      await nextAnimationFrame();

      const targetRect = target.getBoundingClientRect();
      const firstRect = context.selectedGhost.rect;
      const deltaX = targetRect.left - firstRect.left;
      const deltaY = targetRect.top - firstRect.top;
      const scaleX = targetRect.width / firstRect.width;
      const scaleY = targetRect.height / firstRect.height;

      setReverseDebugState('play-animating');

      const layerHeight = context.layer.getBoundingClientRect().height;
      context.otherGhosts.forEach(({ node, distance }) => {
        const delay = (distance - 1) * 40;
        const nodeTop = parseFloat(node.style.top) || 0;
        const nodeHeight = parseFloat(node.style.height) || 0;
        const exitDeltaY = Math.max(30, layerHeight - nodeTop + nodeHeight);
        node.style.transition = `transform 1200ms ease ${delay}ms`;
        node.style.transform = `translateY(${Math.ceil(exitDeltaY)}px)`;
      });

      const selectedNode = context.selectedGhost.node;
      selectedNode.style.transition = 'transform 600ms cubic-bezier(0.25, 1, 0.5, 1) 250ms';
      selectedNode.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

      widget.classList.add('is-closing-play');
      await waitForTransitionEnd(selectedNode, 950);
      setReverseDebugState('complete');
    } finally {
      cleanup();
    }
  }

  function createDrawerSectionSnapshot(sections) {
    const drawerSection = sections?.['cart-drawer'];
    if (!drawerSection || typeof drawerSection !== 'string') return null;
    return {
      html: drawerSection,
      doc: new DOMParser().parseFromString(drawerSection, 'text/html'),
    };
  }

  function getSelectedGiftFromDrawerSnapshot(snapshot, freeSet) {
    if (!snapshot?.doc) return null;
    const doc = snapshot.doc;
    const selected = doc.querySelector('.dr-gifts__carousel-item.is-selected[data-dr-gift-select], .dr-gifts__option.is-selected[data-dr-gift-select]');
    const selectedId = Number(selected?.getAttribute('data-dr-gift-select') || 0);
    return freeSet.has(selectedId) ? selectedId : null;
  }

  function getSelectedGiftFromSections(sections, freeSet) {
    return getSelectedGiftFromDrawerSnapshot(createDrawerSectionSnapshot(sections), freeSet);
  }

  function inferCartStateFromDrawerSnapshot(snapshot, allGiftSet) {
    if (!snapshot?.doc) return null;
    const doc = snapshot.doc;
    const inputs = doc.querySelectorAll('.quantity__input[data-quantity-variant-id]');
    if (!inputs.length) return null;

    const qtyByVariant = new Map();
    let hasPaid = false;
    let itemCount = 0;

    inputs.forEach((input) => {
      const variantId = Number(input.getAttribute('data-quantity-variant-id') || 0);
      const qty = Number(input.getAttribute('value') || input.value || 0);
      if (!variantId || qty <= 0) return;
      qtyByVariant.set(variantId, qty);
      itemCount += qty;
      if (!allGiftSet.has(variantId)) hasPaid = true;
    });

    return { qtyByVariant, hasPaid, itemCount };
  }

  function inferCartStateFromSections(sections, allGiftSet) {
    return inferCartStateFromDrawerSnapshot(createDrawerSectionSnapshot(sections), allGiftSet);
  }

  function inferCartStateFromDom(allGiftSet) {
    const inputs = document.querySelectorAll(
      'cart-drawer .quantity__input[data-quantity-variant-id], #main-cart-items .quantity__input[data-quantity-variant-id]'
    );
    if (!inputs.length) return null;

    let hasPaid = false;
    let itemCount = 0;

    inputs.forEach((input) => {
      const variantId = Number(input.getAttribute('data-quantity-variant-id') || 0);
      const qty = Number(input.value || input.getAttribute('value') || 0);
      if (!variantId || qty <= 0) return;
      itemCount += qty;
      if (!allGiftSet.has(variantId)) hasPaid = true;
    });

    return { hasPaid, itemCount };
  }

  function parseBooleanFlag(value) {
    return value === true || value === 'true' || value === '1';
  }

  function collectLegacyGiftCandidatesFromRoot(root) {
    const candidates = new Set();
    if (!root || typeof root.querySelectorAll !== 'function') return candidates;

    const itemNodes = root.querySelectorAll('[data-dr-variant-id][data-dr-is-gift-hide][data-dr-final-line-price]');
    itemNodes.forEach((node) => {
      const variantId = Number(node.getAttribute('data-dr-variant-id') || 0);
      const finalLinePrice = Number(node.getAttribute('data-dr-final-line-price') || 0);
      const quantity = Number(node.getAttribute('data-dr-item-quantity') || 1);
      const isGiftHide = parseBooleanFlag(node.getAttribute('data-dr-is-gift-hide'));
      if (!variantId || quantity <= 0) return;
      if (isGiftHide && finalLinePrice === 0) {
        candidates.add(variantId);
      }
    });

    return candidates;
  }

  function collectLegacyGiftCandidatesFromSections(sections) {
    const snapshot = createDrawerSectionSnapshot(sections);
    if (!snapshot?.doc) return new Set();
    return collectLegacyGiftCandidatesFromRoot(snapshot.doc);
  }

  function collectLegacyGiftCandidatesFromDom() {
    return collectLegacyGiftCandidatesFromRoot(document);
  }

  function buildGiftCleanupCandidateSet(cart, allGiftSet, drawerSnapshot) {
    const candidates = new Set(allGiftSet);
    const addCandidates = (variantSet) => {
      variantSet.forEach((variantId) => candidates.add(variantId));
    };

    if (cart?.sections) {
      addCandidates(drawerSnapshot?.doc ? collectLegacyGiftCandidatesFromRoot(drawerSnapshot.doc) : collectLegacyGiftCandidatesFromSections(cart.sections));
    }

    addCandidates(collectLegacyGiftCandidatesFromDom());

    return candidates;
  }

  function dispatchCartUpdate(cart, source, sectionsApplied) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return Promise.resolve();
    return publish(PUB_SUB_EVENTS.cartUpdate, {
      source: source || GIFT_ENGINE_SOURCE,
      cartData: cart,
      drGiftsSectionsApplied: !!sectionsApplied,
    });
  }

  // Cart page: its own sections, plus the drawer, which is on the page too and
  // would otherwise keep showing the old gifts. Five is Shopify's limit per request.
  function getCartPageSectionIds() {
    const mainCartItemsId = document.getElementById('main-cart-items')?.dataset?.id;
    if (!mainCartItemsId) return [];

    const mainCartFooterId = document.getElementById('main-cart-footer')?.dataset?.id;
    const drawerSectionId = document.querySelector('cart-drawer') ? 'cart-drawer' : null;
    return [
      ...new Set(
        [mainCartItemsId, 'cart-icon-bubble', 'cart-live-region-text', mainCartFooterId, drawerSectionId].filter(Boolean)
      ),
    ];
  }

  function getSectionHtml(sections, ...keys) {
    const sectionsRenderer = getSectionsRenderer();
    if (sectionsRenderer?.getSectionHtml) {
      return sectionsRenderer.getSectionHtml(sections, ...keys);
    }
    if (!sections || typeof sections !== 'object') return null;
    for (const key of keys) {
      if (typeof key !== 'string' || !key) continue;
      if (typeof sections[key] === 'string' && sections[key]) {
        return sections[key];
      }
    }
    return null;
  }

  function pickSectionsForIntent(config, intent, explicitSections) {
    if (Array.isArray(explicitSections) && explicitSections.length > 0) return explicitSections;
    const cartPageSections = getCartPageSectionIds();
    if (cartPageSections.length > 0) return cartPageSections;
    const inDrawer = !!document.querySelector('cart-drawer');
    if (inDrawer) return ['cart-drawer', 'cart-icon-bubble'];
    return config.sections || ['cart-icon-bubble'];
  }

  function dedupeCodes(codes) {
    const seen = new Set();
    const normalized = [];
    (codes || []).forEach((code) => {
      const candidate = String(code || '').trim().toUpperCase();
      if (!candidate || seen.has(candidate)) return;
      seen.add(candidate);
      normalized.push(candidate);
    });
    return normalized;
  }

  function extractDiscountCodes(cart) {
    const found = [];
    const cartLevel = cart?.cart_level_discount_applications || cart?.discount_applications || [];
    cartLevel.forEach((discount) => {
      found.push(discount?.code || discount?.title || '');
    });
    (cart?.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        found.push(discount?.code || discount?.title || '');
      });
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const app = allocation?.discount_application || allocation;
        found.push(app?.code || app?.title || '');
      });
      (item.discount_allocations || []).forEach((allocation) => {
        const app = allocation?.discount_application || allocation;
        found.push(app?.code || app?.title || '');
      });
    });
    return dedupeCodes(found);
  }

  function isDiscountCodeActive(cart, code) {
    const target = String(code || '').trim().toUpperCase();
    if (!target) return false;
    return extractDiscountCodes(cart).includes(target);
  }

  // Codes attached to the cart, without automatic-discount titles.
  function extractAttachedDiscountCodes(cart) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.extractCartDiscountCodes) {
      return rulesEngine.extractCartDiscountCodes(cart);
    }
    return extractDiscountCodes(cart);
  }

  const COUPON_SUPPRESS_KEY = 'drCoupon.suppressed';
  let couponSwapNote = null;

  function getMainCouponCode(config) {
    return String(config?.coupon?.code || config?.couponCode || '').trim().toUpperCase();
  }

  // The current code stays off for the rest of the visit after the shopper
  // removes it, or after Shopify refuses it, so the cart doesn't fight her.
  function isMainCouponSuppressed(code) {
    if (!code) return false;
    try {
      return sessionStorage.getItem(COUPON_SUPPRESS_KEY) === code;
    } catch (_) {
      return false;
    }
  }

  function setMainCouponSuppressed(code, suppressed) {
    try {
      if (suppressed && code) sessionStorage.setItem(COUPON_SUPPRESS_KEY, code);
      else sessionStorage.removeItem(COUPON_SUPPRESS_KEY);
    } catch (_) {}
  }

  function planCartCoupons(cart, config, requestedCodes) {
    const rulesEngine = getRulesEngine();
    if (!rulesEngine?.planCouponCodes || !isFullCartState(cart)) {
      return { codes: [], needsUpdate: false, removed: [], added: false };
    }
    const mainCode = getMainCouponCode(config);
    return rulesEngine.planCouponCodes({
      cart,
      config,
      requestedCodes,
      suppressMain: isMainCouponSuppressed(mainCode),
    });
  }

  function noteCouponWrite(couponPlan, resultCart, config) {
    if (!couponPlan?.needsUpdate || !isFullCartState(resultCart)) return;
    const mainCode = getMainCouponCode(config);
    if (couponPlan.added && !extractAttachedDiscountCodes(resultCart).includes(mainCode)) {
      console.warn(`[DR Gifts] Shopify did not accept ${mainCode}; not retrying this visit.`);
      setMainCouponSuppressed(mainCode, true);
      return;
    }
    if (couponPlan.removed.length > 0 && extractAttachedDiscountCodes(resultCart).includes(mainCode)) {
      couponSwapNote = { from: couponPlan.removed[0], to: mainCode };
    }
  }

  function renderCouponNote(root) {
    if (!couponSwapNote) return;
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('[data-dr-coupon-note]').forEach((note) => {
      const text = note.querySelector('[data-dr-coupon-note-text]');
      if (!text) return;
      const to = document.createElement('bdi');
      to.textContent = couponSwapNote.to;
      const from = document.createElement('bdi');
      from.textContent = couponSwapNote.from;
      text.replaceChildren('ההטבה בעגלה שלך עודכנה ל-', to, ' במקום ', from);
      note.hidden = false;
    });
  }

  function buildReconcileResult({ cart, sections, changed = false, deferred = false, reason = 'no_changes' }) {
    const result = {
      cart,
      sections: sections || cart?.sections || {},
      changed,
      reason,
    };
    if (deferred) {
      result.deferred = true;
    }
    return result;
  }

  function syncCartStore(cart, meta = {}) {
    if (!isFullCartState(cart)) return null;
    const cartStore = getCartStore();
    if (!cartStore?.replaceCart) return null;
    return cartStore.replaceCart(cart, {
      source: meta.source || GIFT_ENGINE_SOURCE,
      reason: meta.reason || 'reconcile',
      replaceSections: false,
    });
  }

  async function applyCartUpdates(updates, config, opts = {}) {
    const sections = pickSectionsForIntent(config, opts.intent, opts.sections);
    const sectionsUrl = opts.sectionsUrl || window.location.pathname + window.location.search;
    const couponPlan = opts.couponPlan?.needsUpdate ? opts.couponPlan : null;
    const discount = couponPlan ? couponPlan.codes.join(',') : undefined;
    const transport = getTransport();
    const updatedCart = transport?.updateCart
      ? await transport.updateCart(updates, { sections, sectionsUrl, discount })
      : await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates, discount, sections, sections_url: sectionsUrl }),
        }).then(async (response) => {
          if (!response.ok) throw new Error(`Failed to update cart (${response.status})`);
          return response.json();
        });
    noteCouponWrite(couponPlan, updatedCart, config);
    return buildReconcileResult({
      cart: updatedCart,
      sections: updatedCart.sections || {},
      changed: true,
      reason: 'reconcile_write_applied',
    });
  }

  async function reconcilePartialPostAdd(cart, opts, config, freeSet, allGiftSet, existingSections) {
    const addedVariantId = Number(cart.variant_id || cart.id || 0);

    if (allGiftSet.has(addedVariantId)) {
      return buildReconcileResult({
        cart,
        sections: cart.sections || existingSections || {},
        changed: false,
        deferred: true,
        reason: 'reconcile_skipped_partial_post_add',
      });
    }

    const drawerSnapshot = createDrawerSectionSnapshot(cart.sections || existingSections);
    const selectedFromSections = getSelectedGiftFromDrawerSnapshot(drawerSnapshot, freeSet);
    const sectionState = inferCartStateFromDrawerSnapshot(drawerSnapshot, allGiftSet);

    if (!sectionState) {
      const settledCart = await fetchCartForPostAdd(addedVariantId, 2);
      if (settledCart) {
        if (Object.keys(existingSections).length) settledCart.sections = existingSections;
        return { cart: settledCart };
      }
      return buildReconcileResult({
        cart,
        sections: cart.sections || existingSections || {},
        changed: false,
        deferred: true,
        reason: 'reconcile_deferred_no_full_state',
      });
    }

    const freeModuleOn = isModuleEnabled(config, 'free');
    const desiredFree =
      freeModuleOn
        ? (
          (sectionState.hasPaid ? selectedFromSections : null) ||
          config.free[config.defaultFreeIndex] ||
          config.free[0] ||
          null
        )
        : null;

    if (!desiredFree) {
      return buildReconcileResult({
        cart,
        sections: cart.sections || existingSections || {},
        changed: false,
        deferred: true,
        reason: 'reconcile_deferred_no_full_state',
      });
    }

    const updates = {};
    let needsWrite = false;
    for (const variantId of config.free) {
      const currentQty = sectionState.qtyByVariant.get(variantId) || 0;
      const desiredQty = sectionState.hasPaid && variantId === desiredFree ? 1 : 0;
      if (currentQty !== desiredQty) {
        updates[variantId] = desiredQty;
        needsWrite = true;
      }
    }

    if (!needsWrite) {
      return buildReconcileResult({
        cart,
        sections: cart.sections || existingSections || {},
        changed: false,
        deferred: true,
        reason: 'reconcile_deferred_no_full_state',
      });
    }

    return applyCartUpdates(updates, config, {
      intent: opts.intent,
      sections: opts.sections,
      sectionsUrl: opts.sectionsUrl,
    });
  }

  function buildDesiredGiftUpdates(cart, config, allGiftSet, desiredState, drawerSnapshot) {
    const giftCleanupCandidates = buildGiftCleanupCandidateSet(cart, allGiftSet, drawerSnapshot);
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.buildGiftUpdatePlan) {
      const plan = rulesEngine.buildGiftUpdatePlan({
        cart,
        config,
        desiredGiftSelections: {
          free: desiredState?.desiredFree,
          bonus: desiredState?.desiredBonus,
          bonus2: desiredState?.desiredBonus2,
          bonus3: desiredState?.desiredBonus3,
        },
        cleanupCandidateIds: Array.from(giftCleanupCandidates),
      });
      return { updates: plan.updates || {}, needsUpdate: plan.needsUpdate === true };
    }
    const updates = {};
    let needsUpdate = false;
    for (const variantId of giftCleanupCandidates) {
      const currentQty = cart.items.find((item) => item.variant_id === variantId)?.quantity || 0;
      let desiredQty = 0;
      if (variantId === desiredState.desiredFree) {
        desiredQty = 1;
      } else if (variantId === desiredState.desiredBonus) {
        desiredQty = 1;
      } else if (variantId === desiredState.desiredBonus2) {
        desiredQty = 1;
      } else if (variantId === desiredState.desiredBonus3) {
        desiredQty = 1;
      }
      if (currentQty !== desiredQty) {
        updates[variantId] = desiredQty;
        needsUpdate = true;
      }
    }
    return { updates, needsUpdate };
  }

  async function coreReconcile(opts = {}) {
    const config = getConfig();
    const existingSections = opts.cart?.sections || {};
    let cart = opts.cart;
    const intent = opts.intent || 'post-change';

    if (!config) {
      return buildReconcileResult({
        cart,
        sections: cart?.sections || existingSections || {},
        changed: false,
        reason: 'no_config',
      });
    }

    const freeSet = buildFreeGiftIdSet(config);
    const allGiftSet = buildAllGiftIdSet(config);

    if (!isFullCartState(cart)) {
      if (intent === 'select-gift') {
        cart = await fetchCart();
        if (Object.keys(existingSections).length) cart.sections = existingSections;
      }

      if (intent === 'post-add' && isPartialAddState(cart)) {
        const addedVariantId = Number(cart.variant_id || cart.id || 0);
        if (!allGiftSet.has(addedVariantId)) {
          const settledCart = await fetchCartForPostAdd(addedVariantId);
          if (settledCart) {
            if (Object.keys(existingSections).length) settledCart.sections = existingSections;
            cart = settledCart;
          }
        }
      }

      if (intent === 'post-add' && isPartialAddState(cart)) {
        const partialResult = await reconcilePartialPostAdd(cart, opts, config, freeSet, allGiftSet, existingSections);
        if (isFullCartState(partialResult?.cart)) {
          cart = partialResult.cart;
        } else {
          return partialResult;
        }
      }

      if (intent === 'post-add' && !isFullCartState(cart)) {
        return buildReconcileResult({
          cart,
          sections: cart?.sections || existingSections || {},
          changed: false,
          deferred: true,
          reason: 'reconcile_deferred_no_full_state',
        });
      }

      if (!isFullCartState(cart)) {
        cart = await fetchCart();
        if (Object.keys(existingSections).length) cart.sections = existingSections;
      }
    }
    let updates = {};
    let needsUpdate = false;
    if (!opts.couponOnly) {
      const desiredState = computeDesiredGiftState(
        cart,
        config,
        freeSet,
        allGiftSet,
        opts.desiredGiftSelections || opts.desiredFreeGift
      );
      const drawerSnapshot = createDrawerSectionSnapshot(cart.sections || existingSections);
      ({ updates, needsUpdate } = buildDesiredGiftUpdates(cart, config, allGiftSet, desiredState, drawerSnapshot));
    }
    const couponPlan = planCartCoupons(cart, config);

    if (!needsUpdate && !couponPlan.needsUpdate) {
      return buildReconcileResult({
        cart,
        sections: cart.sections || existingSections || {},
        changed: false,
        reason: 'no_changes',
      });
    }

    return applyCartUpdates(needsUpdate ? updates : {}, config, {
      intent: opts.intent,
      sections: opts.sections,
      sectionsUrl: opts.sectionsUrl,
      couponPlan,
    });
  }

  const checkoutSelector = [
    '#CartDrawer-Checkout',
    'cart-drawer button[name="checkout"]',
    'cart-drawer a[href*="/checkout"]',
    '.cart__checkout-button',
  ].join(', ');
  const quantityControlsSelector = [
    'cart-drawer .quantity__input',
    'cart-drawer .quantity__button',
    'cart-drawer cart-remove-button button',
    'cart-items .quantity__input',
    'cart-items .quantity__button',
    'cart-items cart-remove-button button',
  ].join(', ');

  function flushButtonStates() {
    buttonStateFrame = 0;
    const busy = pendingBusyState;
    document.querySelectorAll('[data-dr-gift-select]').forEach((btn) => {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', String(busy));
    });
    document.querySelectorAll('[data-dr-coupon-activate]').forEach((btn) => {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', String(busy));
    });
    document.querySelectorAll('[data-dr-coupon-remove]').forEach((btn) => {
      btn.disabled = busy;
      btn.setAttribute('aria-disabled', String(busy));
    });
    document.querySelectorAll(checkoutSelector).forEach((el) => {
      if (el.tagName === 'BUTTON') {
        el.disabled = busy;
        el.setAttribute('aria-disabled', String(busy));
      } else {
        el.setAttribute('aria-disabled', String(busy));
        el.style.pointerEvents = busy ? 'none' : '';
      }
    });
    document.querySelectorAll(quantityControlsSelector).forEach((el) => {
      el.disabled = busy;
    });
  }

  function updateButtonStates() {
    pendingBusyState = isSystemBusy();
    if (buttonStateFrame) return;
    if (typeof requestAnimationFrame === 'function') {
      buttonStateFrame = requestAnimationFrame(flushButtonStates);
      return;
    }
    flushButtonStates();
  }

  async function executeReconcilePass(opts = {}) {
    const intent = opts.intent || 'post-change';
    const source = opts.source || `${GIFT_ENGINE_SOURCE}:${intent}`;
    const publishEvent = opts.publishEvent !== false;
    const applySections = opts.applySections === true;
    const result = await coreReconcile({
      intent,
      cart: opts.cart,
      sections: opts.sections,
      sectionsUrl: opts.sectionsUrl,
      desiredFreeGift: opts.desiredFreeGift,
      desiredGiftSelections: opts.desiredGiftSelections,
      couponOnly: opts.couponOnly === true,
    });

    const safeCart = result.cart || {};
    safeCart.sections = result.sections || safeCart.sections || {};
    syncCartStore(safeCart, {
      source,
      reason: result.reason || intent,
    });

    let sectionsApplied = false;
    if (applySections && result.changed === true && safeCart.sections) {
      sectionsApplied = renderWithDawn(safeCart);
    }

    if (publishEvent) {
      await dispatchCartUpdate(safeCart, source, sectionsApplied);
    }

    return { ...result, source };
  }

  function reconcileCart(opts = {}) {
    return enqueueReconcilerTask(() => executeReconcilePass(opts));
  }

  function settleCart(opts = {}) {
    return reconcileCart(opts);
  }

  function reconcileIntent(opts = {}) {
    return reconcileCart(opts);
  }

  function ensureReconciled(opts = {}) {
    return reconcileCart({ intent: 'post-change', ...opts });
  }

  function getGiftPool(config, tier) {
    if (tier === 'free') return config?.free || [];
    if (tier === 'bonus') return config?.bonus?.variants || [];
    if (tier === 'bonus2') return config?.bonus2?.variants || [];
    if (tier === 'bonus3') return config?.bonus3?.variants || [];
    return [];
  }

  async function selectGift(tier, variantId, opts = {}) {
    const normalizedTier = String(tier || '').trim();
    const numericId = Number(variantId);
    const config = getConfig();
    if (!numericId || !getGiftPool(config, normalizedTier).includes(numericId)) return Promise.resolve();
    try {
      const result = await reconcileCart({
        intent: 'select-gift',
        desiredGiftSelections: { [normalizedTier]: numericId },
        applySections: true,
        publishEvent: true,
        source: `${GIFT_ENGINE_SOURCE}:select-gift:${normalizedTier}`,
      });
      if (opts.reverseContext) {
        await playReverseMorph(opts.reverseContext);
      }
      return result;
    } catch (err) {
      if (opts.reverseContext) disposeReverseMorphContext(opts.reverseContext);
      throw err;
    }
  }

  function selectFreeGift(variantId, opts = {}) {
    return selectGift('free', variantId, opts);
  }

  function applyDiscountCode(code, opts = {}) {
    const requestedCode = String(code || '').trim().toUpperCase();
    if (!requestedCode) return Promise.resolve({ success: false, cart: null });

    return enqueueReconcilerTask(async () => {
      const mode = opts.mode === 'replace' ? 'replace' : 'append';
      const beforeCart = await fetchCart();
      const config = getConfig() || {};
      const mainCode = getMainCouponCode(config);
      if (requestedCode === mainCode) setMainCouponSuppressed(mainCode, false);
      // Append keeps club codes and DRNEW, swaps retired sitewide codes out.
      const couponPlan = mode === 'append' ? planCartCoupons(beforeCart, config, [requestedCode]) : null;
      const nextCodes =
        mode === 'replace'
          ? [requestedCode]
          : couponPlan?.needsUpdate
          ? couponPlan.codes
          : dedupeCodes([...extractAttachedDiscountCodes(beforeCart), requestedCode]);
      const sections = pickSectionsForIntent(config, 'post-change', opts.sections);
      const sectionsUrl = opts.sectionsUrl || window.location.pathname + window.location.search;
      const transport = getTransport();
      const discountCart = transport?.updateDiscountCodes
        ? await transport.updateDiscountCodes(nextCodes, { sections, sectionsUrl, timeoutMs: opts.timeoutMs })
        : await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount: nextCodes.join(','),
              sections,
              sections_url: sectionsUrl,
            }),
          }).then(async (response) => {
            if (!response.ok) throw new Error(`Failed to apply discount (${response.status})`);
            return response.json();
          });
      noteCouponWrite(couponPlan, discountCart, config);
      const reconcileResult = await coreReconcile({
        intent: 'post-change',
        cart: discountCart,
        sections,
        sectionsUrl,
      });
      const safeCart = reconcileResult.cart || discountCart || {};
      safeCart.sections = reconcileResult.sections || safeCart.sections || {};
      syncCartStore(safeCart, {
        source: `${GIFT_ENGINE_SOURCE}:apply-discount`,
        reason: reconcileResult.reason || 'apply-discount',
      });

      const success = isDiscountCodeActive(safeCart, requestedCode);
      let sectionsApplied = false;
      if (success || reconcileResult.changed === true) {
        sectionsApplied = renderWithDawn(safeCart);
      }
      await dispatchCartUpdate(safeCart, `${GIFT_ENGINE_SOURCE}:apply-discount`, sectionsApplied);

      return { success, cart: safeCart };
    });
  }

  function removeDiscountCode(code, opts = {}) {
    const requestedCode = String(code || '').trim().toUpperCase();
    if (!requestedCode) return Promise.resolve({ success: false, cart: null });

    return enqueueReconcilerTask(async () => {
      const config = getConfig() || {};
      const mainCode = getMainCouponCode(config);
      if (requestedCode === mainCode) setMainCouponSuppressed(requestedCode, true);
      const beforeCart = await fetchCart();
      const existingCodes = extractAttachedDiscountCodes(beforeCart);
      const nextCodes = existingCodes.filter((existingCode) => String(existingCode || '').toUpperCase() !== requestedCode);
      if (nextCodes.length === existingCodes.length) {
        return { success: true, cart: beforeCart };
      }

      const sections = pickSectionsForIntent(config, 'post-change', opts.sections);
      const sectionsUrl = opts.sectionsUrl || window.location.pathname + window.location.search;
      const transport = getTransport();
      const discountCart = transport?.updateDiscountCodes
        ? await transport.updateDiscountCodes(nextCodes, { sections, sectionsUrl, timeoutMs: opts.timeoutMs })
        : await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              discount: nextCodes.join(','),
              sections,
              sections_url: sectionsUrl,
            }),
          }).then(async (response) => {
            if (!response.ok) throw new Error(`Failed to remove discount (${response.status})`);
            return response.json();
          });
      const reconcileResult = await coreReconcile({
        intent: 'post-change',
        cart: discountCart,
        sections,
        sectionsUrl,
      });
      const safeCart = reconcileResult.cart || discountCart || {};
      safeCart.sections = reconcileResult.sections || safeCart.sections || {};
      syncCartStore(safeCart, {
        source: `${GIFT_ENGINE_SOURCE}:remove-discount`,
        reason: reconcileResult.reason || 'remove-discount',
      });

      renderWithDawn(safeCart);
      await dispatchCartUpdate(safeCart, `${GIFT_ENGINE_SOURCE}:remove-discount`, true);

      return { success: !isDiscountCodeActive(safeCart, requestedCode), cart: safeCart };
    });
  }

  function validateFreeGiftState(cart, config, allGiftSet, freeSet) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.validateFreeGiftState) {
      return rulesEngine.validateFreeGiftState(cart, config, allGiftSet, freeSet);
    }
    const freeModuleOn = isModuleEnabled(config, 'free');
    if (!freeModuleOn) {
      // A variant can exist in both "free" pool and bonus tiers. When free module is off,
      // keep only overlap variants that are currently valid as bonus by threshold/module.
      const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
      const allowedBonusIds = new Set();
      if (
        isModuleEnabled(config, 'bonus1') &&
        config.bonus?.variants?.length &&
        eligibleTotal >= (config.bonus?.threshold || 0)
      ) {
        config.bonus.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      if (
        isModuleEnabled(config, 'bonus2') &&
        config.bonus2?.variants?.length &&
        eligibleTotal >= (config.bonus2?.threshold || 0)
      ) {
        config.bonus2.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      if (
        isModuleEnabled(config, 'bonus3') &&
        config.bonus3?.variants?.length &&
        eligibleTotal >= (config.bonus3?.threshold || 0)
      ) {
        config.bonus3.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      const invalidFreeLines = cart.items.filter(
        (item) => freeSet.has(item.variant_id) && !allowedBonusIds.has(item.variant_id)
      );
      return invalidFreeLines.length === 0;
    }
    const hasPaid = cart.items.some((item) => !allGiftSet.has(item.variant_id));
    const freeInCart = cart.items.filter((item) => freeSet.has(item.variant_id));
    if (hasPaid) return freeInCart.length === 1 && freeInCart[0].quantity === 1;
    return freeInCart.length === 0;
  }

  function validateBonusGiftState(cart, config, allGiftSet) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.validateBonusGiftState) {
      return rulesEngine.validateBonusGiftState(cart, config, allGiftSet);
    }
    const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
    const bonusChecks = [
      { pool: config.bonus, moduleOn: isModuleEnabled(config, 'bonus1') },
      { pool: config.bonus2, moduleOn: isModuleEnabled(config, 'bonus2') },
      { pool: config.bonus3, moduleOn: isModuleEnabled(config, 'bonus3') },
    ].filter((check) => check.pool?.variants?.length);

    for (const check of bonusChecks) {
      const poolSet = new Set(check.pool.variants);
      const lines = cart.items.filter((item) => poolSet.has(item.variant_id) && item.quantity > 0);
      const shouldHaveGift = check.moduleOn && eligibleTotal >= check.pool.threshold;
      if (shouldHaveGift) {
        if (lines.length !== 1 || lines[0].quantity !== 1) return false;
      } else if (lines.length > 0) {
        return false;
      }
    }

    return true;
  }

  function resolveCheckoutForm(triggerEl) {
    if (!triggerEl) return null;
    if (triggerEl.tagName === 'FORM') return triggerEl;
    if (triggerEl.form && triggerEl.form.tagName === 'FORM') return triggerEl.form;
    const explicitFormId = triggerEl.getAttribute?.('form');
    if (explicitFormId) {
      const explicitForm = document.getElementById(explicitFormId);
      if (explicitForm && explicitForm.tagName === 'FORM') return explicitForm;
    }
    const closestForm = triggerEl.closest?.('#CartDrawer-Form, form[action="/cart"]');
    return closestForm && closestForm.tagName === 'FORM' ? closestForm : null;
  }

  function resolveCheckoutSubmitter(triggerEl, form) {
    if (!form) return null;
    if (
      triggerEl &&
      triggerEl.tagName === 'BUTTON' &&
      triggerEl.type === 'submit' &&
      triggerEl.name === 'checkout'
    ) {
      return triggerEl;
    }
    if (
      triggerEl &&
      typeof triggerEl.matches === 'function' &&
      triggerEl.matches('button[name="checkout"][type="submit"], button[name="checkout"]:not([type])')
    ) {
      return triggerEl;
    }
    return (
      form.querySelector('button[name="checkout"][type="submit"], button[name="checkout"]:not([type])') ||
      document.querySelector(`button[name="checkout"][form="${form.id}"]`) ||
      null
    );
  }

  function validateCheckoutCart(cart, config, allGiftSet, freeSet) {
    const rulesEngine = getRulesEngine();
    if (rulesEngine?.validateCheckoutCart) {
      return rulesEngine.validateCheckoutCart(cart, config, allGiftSet, freeSet);
    }
    if (!Array.isArray(cart?.items) || cart.items.length === 0) {
      return { ok: false, reason: 'empty_cart' };
    }
    const hasPaid = cart.items.some((item) => !allGiftSet.has(item.variant_id));
    if (!hasPaid && cart.items.length > 0) {
      return { ok: false, reason: 'gift_only_cart' };
    }
    if (!validateFreeGiftState(cart, config, allGiftSet, freeSet)) {
      return { ok: false, reason: 'free_gift_invalid' };
    }
    if (!validateBonusGiftState(cart, config, allGiftSet)) {
      return { ok: false, reason: 'bonus_gift_invalid' };
    }
    return { ok: true, reason: 'valid' };
  }

  function settleForCheckout(opts = {}) {
    return enqueueSystemTask(async () => {
      const config = getConfig();
      if (!config) {
        return { ok: true, cart: null, changed: false, reason: 'no_config' };
      }

      const freeSet = buildFreeGiftIdSet(config);
      const allGiftSet = buildAllGiftIdSet(config);
      let cart = isFullCartState(opts.cart) ? opts.cart : await fetchCart();
      // Always normalize gift lines once before final validation so recovered
      // stale gifts cannot bypass checkout just because the current cart happens
      // to look valid under today's pool.
      const reconcileResult = await coreReconcile({
        intent: 'checkout-validate',
        cart,
        sections: opts.sections,
        sectionsUrl: opts.sectionsUrl,
      });
      if (isFullCartState(reconcileResult?.cart)) {
        cart = reconcileResult.cart;
      } else {
        cart = await fetchCart();
      }

      const validation = validateCheckoutCart(cart, config, allGiftSet, freeSet);
      syncCartStore(cart, {
        source: `${GIFT_ENGINE_SOURCE}:checkout-validate`,
        reason: validation.reason || 'checkout-validate',
      });
      return {
        ok: validation.ok,
        cart,
        changed: !!reconcileResult?.changed,
        reason: validation.reason,
      };
    });
  }

  async function hardGateCheckout(e, triggerEl) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    if (activeCheckoutPromise) return activeCheckoutPromise;
    armCheckout();

    activeCheckoutPromise = (async () => {
      const config = getConfig();
      if (!config) {
        proceedToCheckout(triggerEl);
        return;
      }

      try {
        await whenIdle();
        const cartPageSections = getCartPageSectionIds();
        const settlement = await settleForCheckout({
          sections: cartPageSections.length > 0 ? cartPageSections : undefined,
          sectionsUrl: window.location.pathname + window.location.search,
        });
        if (!settlement?.ok) {
          disarmCheckout();
          return;
        }

        proceedToCheckout(triggerEl);
      } catch (err) {
        console.error('[DR Gifts] Checkout blocked due to error:', err);
        disarmCheckout();
      }
    })().finally(() => {
      activeCheckoutPromise = null;
    });

    return activeCheckoutPromise;
  }

  function proceedToCheckout(triggerEl) {
    const form = resolveCheckoutForm(triggerEl);
    if (form) {
      if (form.id === 'CartDrawer-Form' || form.id === 'cart') {
        window.location.href = '/checkout';
        return;
      }
      const submitter = resolveCheckoutSubmitter(triggerEl, form);
      form.dataset.drAllowSubmit = '1';
      if (submitter && typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
        return;
      }
      if (submitter?.name === 'checkout') {
        const hiddenCheckoutInput = document.createElement('input');
        hiddenCheckoutInput.type = 'hidden';
        hiddenCheckoutInput.name = 'checkout';
        hiddenCheckoutInput.value = submitter.value || '';
        hiddenCheckoutInput.dataset.drCheckoutHidden = 'true';
        form.appendChild(hiddenCheckoutInput);
      }
      form.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  function openGiftPicker(giftCard) {
    if (!giftCard || giftCard.dataset.drPickerEnabled !== 'true') return;
    const widget = giftCard.closest('.dr-gifts');
    if (!widget || widget.classList.contains('is-picker-open')) return;
    const tier = String(giftCard.dataset.drGiftTier || 'free');
    const pickerGroup = widget.querySelector(`[data-dr-picker-group="${tier}"]`);
    if (!pickerGroup) return;
    widget.querySelectorAll('[data-dr-picker-group]').forEach((group) => {
      group.toggleAttribute('hidden', group !== pickerGroup);
    });
    pickerGroup.querySelectorAll('.dr-gifts__carousel-item.flip-done').forEach((item) => {
      item.classList.remove('flip-done');
    });
    widget.dataset.drActivePicker = tier;

    const viewProduct = giftCard.querySelector('.dr-gifts__view-product');
    const selectedCarouselItem = pickerGroup.querySelector('.dr-gifts__carousel-item.is-selected .dr-gifts__carousel-product') ||
      pickerGroup.querySelector('.dr-gifts__carousel-item .dr-gifts__carousel-product');
    
    if (!viewProduct || !selectedCarouselItem) {
      widget.classList.add('is-picker-open');
      return;
    }

    // 1. First: Measure initial state
    const firstRect = viewProduct.getBoundingClientRect();

    // 2. Last: Apply class to trigger layout change
    widget.classList.add('is-picker-open');

    // Force layout recalculation
    const _ = widget.offsetHeight;

    // Measure final state
    const lastRect = selectedCarouselItem.getBoundingClientRect();

    // 3. Invert: Calculate differences
    const deltaX = firstRect.left - lastRect.left;
    const deltaY = firstRect.top - lastRect.top;
    const scaleX = firstRect.width / lastRect.width;
    const scaleY = firstRect.height / lastRect.height;

    // Apply inverted transform to make it look like it's in the original position
    const carouselItemBtn = selectedCarouselItem.closest('.dr-gifts__carousel-item');
    
    // Temporarily disable transitions for the setup
    carouselItemBtn.style.transition = 'none';
    carouselItemBtn.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;
    carouselItemBtn.style.transformOrigin = 'top left';
    carouselItemBtn.style.zIndex = '10';

    // 4. Play: Animate to final state
    requestAnimationFrame(() => {
      // Need a second frame for the browser to register the initial transform
      requestAnimationFrame(() => {
        carouselItemBtn.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
        carouselItemBtn.style.transform = 'translate(0, 0) scale(1, 1)';
        
        // Cleanup after animation
        setTimeout(() => {
          carouselItemBtn.style.transition = '';
          carouselItemBtn.style.transform = '';
          carouselItemBtn.style.transformOrigin = '';
          carouselItemBtn.style.zIndex = '';
          carouselItemBtn.classList.add('flip-done');
        }, 400);
      });
    });
  }

  document.addEventListener('click', (e) => {
    const giftCard = e.target.closest('[data-dr-gift-card]');
    if (!giftCard) return;
    openGiftPicker(giftCard);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const giftCard = e.target.closest('[data-dr-gift-card][data-dr-picker-enabled="true"]');
    if (!giftCard) return;
    e.preventDefault();
    openGiftPicker(giftCard);
  });

  document.addEventListener('click', (e) => {
    const giftBtn = e.target.closest('[data-dr-gift-select]');
    if (!giftBtn) return;
    const variantId = Number(giftBtn.dataset.drGiftSelect);
    const tier = String(giftBtn.dataset.drGiftTier || 'free');
    const isCurrentSelection = giftBtn.classList.contains('is-selected') || giftBtn.getAttribute('aria-pressed') === 'true';
    if (!variantId) return;
    e.preventDefault();
    setReverseDebugState('select-click', `${tier}:${variantId}`);
    let reverseContext = null;
    try {
      reverseContext = captureReverseMorphContext(giftBtn);
    } catch (captureErr) {
      setReverseDebugState('capture-error');
      console.warn('[DR Gifts] Reverse capture failed, continuing without animation:', captureErr);
    }

    if (isCurrentSelection) {
      if (reverseContext) {
        playReverseMorph(reverseContext).catch((err) => {
          console.warn('[DR Gifts] Reverse animation failed:', err);
        });
      } else {
        getActiveGiftsWidget()?.classList.remove('is-picker-open');
      }
      return;
    }

    selectGift(tier, variantId, { reverseContext }).catch((err) => {
      console.warn('[DR Gifts] Gift selection failed:', err);
    });
  });

  document.addEventListener('click', (e) => {
    const activateBtn = e.target.closest('[data-dr-coupon-activate]');
    if (!activateBtn) return;
    const configuredCode =
      String(activateBtn.getAttribute('data-dr-coupon-code') || '').trim() ||
      String(window.DR_COUPON_REMINDER?.code || '').trim() ||
      String(getConfig()?.couponCode || '').trim() ||
      'DR46';
    e.preventDefault();
    applyDiscountCode(configuredCode, { mode: 'append' }).catch((err) => {
      console.warn('[DR Gifts] Coupon activation failed:', err);
    });
  });

  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-dr-coupon-remove]');
    if (!removeBtn) return;
    const code = String(removeBtn.getAttribute('data-dr-coupon-remove') || '').trim();
    if (!code) return;
    e.preventDefault();
    e.stopPropagation();
    removeDiscountCode(code).catch((err) => {
      console.warn('[DR Gifts] Coupon removal failed:', err);
    });
  });

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target.closest('#CartDrawer-Form, form[action="/cart"]');
      if (!form) return;
      if (form.dataset.drAllowSubmit === '1') {
        const hiddenCheckoutInput = form.querySelector('input[data-dr-checkout-hidden="true"]');
        if (hiddenCheckoutInput) hiddenCheckoutInput.remove();
        form.dataset.drAllowSubmit = '';
        return;
      }
      hardGateCheckout(e, e.submitter || form);
    },
    true
  );

  document.addEventListener(
    'click',
    (e) => {
      const trigger = e.target.closest(
        ['#CartDrawer-Checkout', 'cart-drawer a[href*="/checkout"]', 'cart-drawer button[name="checkout"]', '.cart__checkout-button'].join(', ')
      );
      if (!trigger) return;
      if (trigger.tagName === 'BUTTON' && trigger.type === 'submit' && trigger.closest('form')) return;
      hardGateCheckout(e, trigger);
    },
    true
  );

  // A returning cart can still hold a retired sitewide code, or miss the
  // current one. Settle its codes once on page load, but only when the
  // server-rendered cart state says a write is likely.
  function settleCouponsOnLoad() {
    const config = getConfig();
    const cartState = window.DR_GIFTS_CONFIG?.cartState;
    if (!config || !cartState || !(Number(cartState.itemCount) > 0)) return;
    const policy = config.coupon || {};
    const mainCode = getMainCouponCode(config);
    const applied = new Set(dedupeCodes(cartState.appliedCodes));
    const hasRetired = (policy.retiredCodes || []).some((code) => applied.has(code));
    const missingMain =
      policy.autoApply === true &&
      !!mainCode &&
      !applied.has(mainCode) &&
      !isMainCouponSuppressed(mainCode) &&
      !(policy.exclusiveCodes || []).some((code) => applied.has(code));
    if (!hasRetired && !missingMain) return;

    const run = () => {
      reconcileCart({
        intent: 'page-load',
        couponOnly: true,
        applySections: true,
        publishEvent: true,
        source: `${GIFT_ENGINE_SOURCE}:page-load-coupons`,
      }).catch((err) => {
        console.warn('[DR Gifts] Coupon settle on load failed:', err);
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 300);
    }
  }

  settleCouponsOnLoad();

  window.drGiftsReconciler = {
    reconcileCart,
    reconcileIntent,
    ensureReconciled,
    planAtomicAddGiftLines,
    selectGift,
    selectFreeGift,
    applyDiscountCode,
    removeDiscountCode,
    isDiscountCodeActive,
    beginExternalMutation,
    runTrackedMutation,
    whenIdle,
    armCheckout,
    disarmCheckout,
    settleForCheckout,
    settleCart,
    renderWithDawn,
    afterCartSectionsRendered,
    requestMembersReopen,
    readMembersUiState,
    writeMembersUiState,
    restoreMembersUiState,
    get isBusy() {
      return isSystemBusy();
    },
    get isCheckoutArmed() {
      return checkoutArmed;
    },
    getConfig,
  };
})();
